/**
 * BankAccountSelect — selector buscable de cuenta Banco/Caja para registrar
 * pagos y cobros.
 *
 * Reemplaza al <select> básico: usa Autocomplete (búsqueda en vivo por código y
 * nombre). Lista cuentas imputables y activas de tipo Activo (bancos/caja) o
 * Pasivo (tarjetas/líneas de crédito, ej. la 2011002), que son las que se usan
 * como origen/destino de un pago. Excluye gasto/ingreso/patrimonio.
 *
 * Valor vacío = "usar cuenta por defecto de Contabilidad".
 */
import { useMemo } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { useAccounts } from '../hooks/useAccounting'

const ALLOWED_TYPES = new Set(['asset', 'liability'])

interface Props {
  value: string
  onChange: (id: string) => void
  placeholder?: string
  disabled?: boolean
}

export function BankAccountSelect({
  value,
  onChange,
  placeholder = '— Usar cuenta por defecto —',
  disabled = false,
}: Props) {
  const { data } = useAccounts({ is_imputable: 'true', is_active: 'true' })

  const options = useMemo(() => {
    const list = data?.accounts ?? []
    return list
      .filter(a => ALLOWED_TYPES.has(a.account_type))
      .map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))
  }, [data])

  return (
    <EntityAutocomplete aria-label="Cuenta bancaria"
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowClear
      clearLabel="Usar cuenta por defecto"
      disabled={disabled}
    />
  )
}
